import {Box, Tab, Tabs} from '@mui/material';
import {ReactNode, useEffect, useState} from 'react';
import './CreateProjectPage.scss';

import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import CreateProjectHeader from './CreateProjectHeader';
import SettingsTab from './SettingsTab';
import GeneralTab from './GeneralTab';
import {useDispatch, useSelector} from 'react-redux';
import {
  setAnalyticsAutomationSelected,
  setAnalyticsComponentsSelected,
  setAnalyticsEngine,
  setEditValues,
  setGenerativeAISelected,
  setIsGenerativeAIActive,
  setRedactionPropagationDisable,
  setSelectedClient,
  setSelectedProject,
  setServerSelected,
  setShowMessage,
  setShowToastSave,
  setShowType,
} from '../../../redux/slice/projectFormSlice';
import {RootState} from '../../../redux/store';
import {
  mapFormToAddProjectPayload,
  validateProjectForm,
} from '../validateProjectForm';
import {
  useAddProject,
  useCreateProjectMeta,
  useEditProjectDetails,
} from '../../../hooks/Projects/useProjects';
import ArrModal from '../../common/arr-modal/ArrModal';
import {setDomainData} from '../../../redux/slice/domainDataSlice';
import {
  availableComponentList,
  baseAutomationList,
  displayedGenerativeAIList,
} from './projectUtility';
import {UserMappingItem} from '../../../api/types/project.type';
import {ToastType} from '../../../shared/constants';
import {useNavigate} from 'react-router-dom';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`create-project-page-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`create-project-page-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const CreateProject: React.FC = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [showDocModal, setShowDocModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastError, setToastError] = useState<string>('');
  const [toastType, setToastType] = useState<ToastType>(ToastType.Error);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSelectedClient(''));
  }, [dispatch]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const form = useSelector((state: RootState) => state.projectForm);
  const selectedClientId = form.selectedClientId;
  const type = form.type;

  const isFormValid = validateProjectForm(form, roleId);

  const {mutate: addProject, error: createError, isPending} = useAddProject();
  const {data: metaData} = useCreateProjectMeta(type ?? '', roleId);

  useEffect(() => {
    if (
      metaData?.analyticsEngines &&
      type !== 'edit' &&
      roleId === -1 &&
      form.analyticsEngine === null
    ) {
      const selectedValue = metaData.analyticsEngines.find(
        (value: UserMappingItem) =>
          value.value.toLowerCase() ===
          t('projectManagement.settingsTab.consilioAnalytics').toLowerCase()
      );

      if (selectedValue) {
        const selectedAnalyticEngine = {
          value: selectedValue.key,
          label: selectedValue.value,
        };

        dispatch(setAnalyticsEngine(selectedAnalyticEngine));
      }
    }
  }, [metaData?.analyticsEngines]);

  const encryptedProjectId = form.encryptedProjectId;
  const {data, isLoading} = useEditProjectDetails(encryptedProjectId);

  const confirmPopup = () => {
    setShowDocModal(false);
    handleSave();
  };

  const handleSaveCheck = () => {
    if (type !== 'edit') {
      handleSave();
      return;
    }

    const {docIDPrefix, docIDSuffix, docIDMinLengthNo} = data.projectSettings;

    const hasAnyChange =
      form.prefix !== docIDPrefix ||
      form.suffix !== docIDSuffix ||
      form.minLength !== docIDMinLengthNo;

    if (hasAnyChange) {
      setShowDocModal(true);
    } else {
      handleSave();
    }
  };

  useEffect(() => {
    if (data) {
      dispatch(setEditValues(data));
      const domainData = {
        projectServers: data?.projectWorkspace,
        loadServers: data?.loadWorkspace,
        productionServers: data?.productionWorkspace,
      };
      dispatch(setDomainData(domainData));

      const preselectedProjectFile = data?.projectWorkspace
        ?.filter((item: UserMappingItem) => item.isSelected)
        .map((item: UserMappingItem) => ({
          id: Number(item.value),
          label: String(item.text),
        }));
      const preselectedLoadFile = data?.loadWorkspace
        ?.filter((item: UserMappingItem) => item.isSelected)
        .map((item: UserMappingItem) => ({
          id: Number(item.value),
          label: String(item.text),
        }));
      const preselectedProductionFile = data?.productionWorkspace
        ?.filter((item: UserMappingItem) => item.isSelected)
        .map((item: UserMappingItem) => ({
          id: Number(item.value),
          label: String(item.text),
        }));

      dispatch(
        setServerSelected({type: 'project', selected: preselectedProjectFile})
      );
      dispatch(
        setServerSelected({type: 'load', selected: preselectedLoadFile})
      );
      dispatch(
        setServerSelected({
          type: 'production',
          selected: preselectedProductionFile,
        })
      );

      if (data?.projectSettings?.canTextualAnalytics) {
        dispatch(setAnalyticsComponentsSelected(availableComponentList));
      }

      const selectedGenAI = [];

      if (data?.projectSettings?.aiInvestgate) {
        selectedGenAI.push(
          displayedGenerativeAIList.find((item) => item.id === 0)!
        );
      }
      if (data?.projectSettings?.aiReview) {
        selectedGenAI.push(
          displayedGenerativeAIList.find((item) => item.id === 1)!
        );
      }
      if (data?.projectSettings?.aiBatchSummarize) {
        selectedGenAI.push(
          displayedGenerativeAIList.find((item) => item.id === 2)!
        );
      }

      if (data?.projectSettings?.canUseGenAI) {
        dispatch(setIsGenerativeAIActive(data?.projectSettings?.canUseGenAI));
        dispatch(setGenerativeAISelected(selectedGenAI));
      } else {
        dispatch(setIsGenerativeAIActive(false));
        dispatch(setGenerativeAISelected([]));
      }

      const selectedAutomations = [];

      if (data?.projectSettings?.autoAnalytics) {
        selectedAutomations.push(
          baseAutomationList.find((item) => item.id === 2)!
        );
      }

      if (data?.projectSettings?.autoIncrementalAnalytics) {
        selectedAutomations.push(
          baseAutomationList.find((item) => item.id === 3)!
        );
      }

      if (selectedAutomations.length > 0) {
        dispatch(setAnalyticsAutomationSelected(selectedAutomations));
      }
      if (data?.projectDetails?.templateProjectID) {
        const matchedTemplate = data?.projectTemplateList.find(
          (item: UserMappingItem) =>
            item.key === Number(data?.projectDetails?.templateProjectID)
        );
        const selectedExistingProject = {
          key: matchedTemplate.key,
          displayValue: matchedTemplate.value,
        };
        dispatch(setSelectedProject(selectedExistingProject));
      } else {
        const selectedExistingProject = {
          key: data?.projectTemplateList[0]?.key,
          displayValue: data?.projectTemplateList[0]?.value,
        };
        dispatch(setSelectedProject(selectedExistingProject));
      }

      if (data?.projectSettings?.analyticsEngineID) {
        const matchedTemplate = data?.analyticsEngines.find(
          (item: UserMappingItem) =>
            item.key === Number(data?.projectSettings?.analyticsEngineID)
        );
        if (matchedTemplate) {
          const selectedAnalyticEngine = {
            value: matchedTemplate.key,
            label: matchedTemplate.value,
          };
          dispatch(setAnalyticsEngine(selectedAnalyticEngine));
        }
      }
      const redacionToggle = data?.projectSettings?.isRedactionPropagated;
      dispatch(setRedactionPropagationDisable(!redacionToggle));
    }
  }, [data]);

  const handleSave = () => {
    const payload = mapFormToAddProjectPayload(form, selectedClientId, type);
    addProject(payload, {
      onSuccess: (data) => {
        dispatch(setShowToastSave(true));
        dispatch(setShowType(ToastType.Success));
        dispatch(setShowMessage(data?.message));
        navigate('/Project/Project');
      },
      onError: (error: any) => {
        if (error.code?.toUpperCase() === 'ECONNABORTED') {
          dispatch(setShowToastSave(true));
          dispatch(setShowType(ToastType.Success));
          dispatch(
            setShowMessage(
              t('projectManagement.createProjectModule.projectCreateMessage')
            )
          );
          navigate('/Project/Project');
          return;
        }
        setShowToast(true);
        setToastType(ToastType.Error);
        if (error?.response?.data?.detail) {
          const trimmedMessaged = error?.response?.data?.detail.split('\n')[0];
          setToastError(trimmedMessaged);
        } else {
          setToastError(createError?.message ?? '');
        }
      },
    });
  };

  const showUnSaveModalHandler = () => {
    navigate('/Project/Project');
  };
  return (
    <Box className="create-project-container">
      <CreateProjectHeader />
      <div className="tab-header" data-testid={`create-project-page-wrapper`}>
        {roleId === -1 ? (
          <>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="create-project-tabs"
              className="create-project-tabs"
            >
              <Tab
                label="General"
                className="create-project-tab-label typo-text subhead4_size"
                {...a11yProps(0)}
              />
              <Tab
                label="Settings"
                className="create-project-tab-label typo-text subhead4_size"
                {...a11yProps(1)}
              />
            </Tabs>

            <TabPanel value={value} index={0}>
              <GeneralTab />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <SettingsTab data={metaData} />
            </TabPanel>
          </>
        ) : (
          <GeneralTab />
        )}
      </div>
      <div
        className="action-section"
        data-testid={`create-project-page-cancel-save-wrapper`}
      >
        <ArrButton
          name={t('common.cancel')}
          id={t('common.cancel')}
          variant="secondary"
          size="large"
          onClick={showUnSaveModalHandler}
          dataTestId="create-project-page-cancel-button"
          className="cancel-button"
        >
          {t('common.cancel')}
        </ArrButton>
        <ArrButton
          name={t('common.save')}
          id={t('common.save')}
          variant="primary"
          size="large"
          onClick={handleSaveCheck}
          dataTestId="save-button"
          className="save-button"
          disabled={!isFormValid || isPending}
          loading={isPending}
        >
          {t('common.save')}
        </ArrButton>
      </div>
      <ArrModal
        showModal={showDocModal}
        showClose={false}
        onClose={() => setShowDocModal(false)}
        title={t('projectManagement.editProject.docIdTitle')}
        description={t('projectManagement.editProject.docIdPopupDescription')}
        dataTestId="projectpage-modal"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => setShowDocModal(false),
            variant: 'secondary',
            dataTestId: 'ppm-cancel-btn',
          },
          {
            id: '2',
            label: t('common.ok'),
            onClick: () => confirmPopup(),
            variant: 'primary',
            dataTestId: 'ppm-confirm-btn',
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastError ?? ''}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
      <ArrLoadingSpinner isOpen={isLoading} overlay />
    </Box>
  );
};

export default CreateProject;
